import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { FilterBase } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-base';
import {
  FilterNumberRange,
  NumberRangeValue,
} from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-number-range.model';
import { createNumberRangeValidator } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/number-range-validator';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { NimbusFilterNumberRangeInputComponent } from '../nimbus-filter-number-range-input/nimbus-filter-number-range-input.component';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-filter-number-range',
  templateUrl: './nimbus-filter-number-range.component.html',
  styleUrls: ['./nimbus-filter-number-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterNumberRangeComponent extends FilterBase<NumberRangeValue, FilterNumberRange> {
  @ViewChild('minControl')
  public minControl: NimbusFilterNumberRangeInputComponent;

  protected valuesEmpty: NumberRangeValue[] = [{ min: null, max: null }];

  public form: FormGroup;
  public hasError: boolean = false;

  public constructor(
    private readonly translateService: TranslateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    formBuilder: FormBuilder,
  ) {
    super();
    this.form = formBuilder.group(
      {
        min: [null],
        max: [null],
      },
      { validators: [createNumberRangeValidator()] },
    );

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });
  }

  public onAttach(): void {
    of({})
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      .pipe(delay(100), untilDestroyed(this))
      .subscribe(() => {
        this.minControl?.onFocusIn();
      });
  }

  public onDetach(): void {
    const raw = this.form.getRawValue();
    const min = raw.min == null || raw.min === '' ? null : Number(raw.min);
    const max = raw.max == null || raw.max === '' ? null : Number(raw.max);

    // An untouched range still sits at the default bounds — treat that as "no filter".
    const isDefaultRange = min === (this.filter?.min ?? null) && max === (this.filter?.max ?? null);
    this.filterValue = isDefaultRange ? { min: null, max: null } : { min, max };

    this.filterValueChanged.emit(this.filterValue);
    this.summary = this.getSummary();
    this.changeDetectorRef.detectChanges();
  }

  protected getSummary(): string {
    const min = this.filterValue?.min;
    const max = this.filterValue?.max;

    if (min == null && max == null) {
      this.hasError = false;
      return this.translateService.instant(TranslationKeys.FilterAny);
    }

    this.form.updateValueAndValidity();
    this.hasError = this.form.invalid;
    if (this.form.invalid) {
      return 'Invalid Range Selected';
    }

    const unit = this.filter?.unit ?? '';
    const minDisplay = min == null ? '' : `${min}${unit}`;
    const maxDisplay = max == null ? '' : `${max}${unit}`;
    return `${minDisplay} - ${maxDisplay}`;
  }

  protected onFilterValueChanged(): void {
    const min = this.filterValue?.min ?? this.filter?.min ?? null;
    const max = this.filterValue?.max ?? this.filter?.max ?? null;
    this.form.patchValue({ min, max }, { emitEvent: false });
  }
}
